import type { Snippet } from "@/types/snippet";

import { getMockMarketProfile } from "../market";

import { createId, createPastTimestamp } from "./base";

type SnippetConfig = Pick<Snippet, "name" | "content">;

/**
 * Saved replies the demo organization would actually keep on file. The brand
 * comes from the market profile rather than a literal, so a snippet never names
 * a business the rest of the workspace has never heard of.
 */
function snippetSeeds(): SnippetConfig[] {
  const { organizationName } = getMockMarketProfile();

  return [
    {
      name: "Appointment Confirmation",
      content:
        "Hi {name}! Your appointment has been confirmed for {date} at {time}. Please arrive 15 minutes early. If you need to reschedule, please let us know at least 24 hours in advance.",
    },
    {
      name: "Appointment Reminder",
      content:
        "Hi {name}, this is a friendly reminder about your appointment tomorrow at {time}. We look forward to seeing you!",
    },
    {
      name: "Welcome Message",
      content: `Hello and welcome to ${organizationName}! How can we assist you today? You can ask about appointments, services, or pricing.`,
    },
    {
      name: "After Hours",
      content:
        "Thank you for contacting us! Our office hours are Monday-Friday 9am-6pm. We'll get back to you as soon as we're open. For emergencies, please call our emergency line.",
    },
    {
      name: "Service Info Request",
      content:
        "Thank you for your interest in {service}! I'd be happy to provide more information. Would you like to schedule a free consultation to discuss your options?",
    },
    {
      name: "Insurance Verification",
      content:
        "To verify your insurance coverage, please provide your insurance provider name, policy number, and date of birth. We'll check your benefits and get back to you.",
    },
    {
      name: "Payment Plans",
      content:
        "We offer flexible payment plans to keep care affordable. We accept most major insurance plans and offer financing options. Would you like to learn more?",
    },
    {
      name: "Thank You",
      content: `Thank you for choosing ${organizationName}! We appreciate your trust in our team. Please don't hesitate to reach out if you have any questions.`,
    },
  ];
}

/**
 * Generate mock snippets
 */
export function generateSnippets(count: number = 8): Snippet[] {
  return snippetSeeds()
    .slice(0, count)
    .map((config, index) => ({
      id: createId(),
      name: config.name,
      content: config.content,
      attachments: [],
      isPrivate: index % 3 === 0,
      createdAt: createPastTimestamp(60 - index * 5),
      lastUsedAt: index < 2 ? createPastTimestamp(index + 1) : null,
    }));
}
